<template>
    <div :style="isUpdatingShippingMethod ? shipmentUpdatingStyle : shipmentIdleStyle">
        <div v-if="isLoading" class="text-center">
            <span class="spinner-border text-secondary" role="status" aria-hidden="true"></span>
        </div>

        <div v-if="isError" class="alert alert-danger mb-3" role="alert">
            {{ $t('checkout.shipping_methods_error') }}
            <button class="btn btn-sm btn-outline-danger ms-2" @click="init">
                {{ $t('checkout.retry') }}
            </button>
        </div>

        <div
            v-if="!isLoading && !isError"
            v-for="(shippingMethod, index) in shippingMethods"
            :key="shippingMethod.id"
			class="d-flex"
			:class="{ 'mt-1': index !== shippingMethods.length - 1 }"
        >
            <div
                class="form-check"
            >
                <input
                    class="form-check-input"
                    type="radio"
                    name="delivery"
                    :id="shippingMethod.id"
                    v-model="selectedShippingMethodId"
                    :value="shippingMethod.id"
                    @change="updateShippingMethod(shippingMethod.id)"
                    :disabled="isUpdatingShippingMethod"
                />
                <label class="form-check-label" :for="shippingMethod.id">
                    <strong>{{ shippingMethod.translated?.name }}</strong>
                    <span
                        v-if="pendingShippingMethodId === shippingMethod.id"
                        class="spinner-border spinner-border-sm ms-2 align-middle text-secondary"
                        role="status"
                        aria-hidden="true"
                    ></span>
                    <span class="d-block small mb-0" v-if="shippingMethod.translated?.description">
                        {{ shippingMethod.translated?.description }}
                    </span>
                </label>
            </div>

			<div class="ms-auto">
				<div v-if="prices[shippingMethod.id] === null || prices[shippingMethod.id] === undefined" class="shipping-price fw-bold me-auto">
					<span class="spinner-border spinner-border-sm text-secondary" role="status" aria-hidden="true"></span>
				</div>
				<div v-else-if="prices[shippingMethod.id] > 0" class="shipping-price fw-bold me-auto">
					<span>&euro;{{ formatNumber(prices[shippingMethod.id]) }}</span>
				</div>
				<div v-else class="shipping-price fw-bold me-auto text-success">
					{{ $t('checkout.free_shipping') }}
				</div>
			</div>
        </div>
    </div>
</template>

<script>
import shopware from '../../mixins/shopware';
import formatters from '../../mixins/formatters';
import { EventBus } from '../../api/eventBus.ts';

export default {
    mixins: [shopware, formatters],
    props: {
        cart: Object|null,
        dealerSelectionUrl: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            shippingMethods: [],
            isLoading: false,
            isError: false,
            isUpdatingShippingMethod: false,
            selectedShippingMethodId: null,
            pendingShippingMethodId: null,
            internalCart: null,
            retryCount: 0,
            maxRetries: 3,
            customerWatcherTimeout: null,
            cartTotalWatcherTimeout: null,
            lastEnrichmentFingerprint: null,
            enrichmentRunId: 0,
            prices: {},
            // Tracks the in-flight enrichment context-probe (PATCH /context + GET /cart) so updateShippingMethod()
            // can await it before sending the user's own PATCH. Without this, the probe's PATCH for a non-active
            // method could land server-side AFTER the user's PATCH and leave the cart's active method desynced
            // from the radio. Cleared in finally; null when no probe is active.
            currentContextProbePromise: null
        };
    },
    computed: {
        cartData() {
            return this.internalCart || this.cart;
        },
        // Captures inputs that affect rule-based shipping prices for non-active methods (cart total + country).
        //
        // Deliberately does NOT include activeMethodId. A non-active method's accurate price depends only on the
        // cart's total and rules — NOT on which method the customer currently has selected. Including it would
        // cause the cached _accuratePrice to be invalidated whenever the user switches shipping method, even
        // though the cached value is still semantically correct, leading to a 1-3s UI flicker where rebuildPrices
        // falls through to the rule-blind calculateFallbackPrice() until background enrichment confirms the same
        // value. (Active method's price comes live from cart.deliveries[0].shippingCosts, no caching needed.)
        enrichmentFingerprint() {
            const total = this.cartData?.price?.totalPrice ?? null;
            if (total === null) return null;
            // Goods total = sum of line items where isGood (excludes promotion/voucher line items). Rules like
            // `cartGoodsPrice` evaluate against this, so when the goods change — e.g. a voucher adds a product
            // and a discount line item that exactly cancel out — we MUST invalidate cached _accuratePrice even
            // though `totalPrice` stays the same (often €0 in voucher-fully-covers scenarios). Without this,
            // an early enrichment cycle that probed an empty cart and cached €3.95 fallback would stick after
            // the voucher is applied (fingerprint unchanged → no re-enrichment → stale display).
            const goodsTotal = (this.cartData?.lineItems || [])
                .filter(li => li.good !== false)
                .reduce((sum, li) => sum + (li.price?.totalPrice ?? 0), 0);
            const countryId = this.cartData?.deliveries?.[0]?.shippingOrderAddress?.countryId
                ?? this.context?.customer?.activeShippingAddress?.countryId
                ?? null;
            return `${total}|${goodsTotal}|${countryId}`;
        },
        hasDealerOption() {
            return this.shippingMethods.some(method => method.technicalName === 'shipping_dealer');
        },
        isNoDealerSelected() {
            return this.selectedShippingMethodId &&
                this.hasDealerOption &&
                this.shippingMethods.find(m => m.id === this.selectedShippingMethodId)?.technicalName === 'shipping_dealer' &&
                (!this.cartData?.extensions?.dealer);
        },
        shipmentUpdatingStyle() {
            return {
                opacity: 0.5,
                pointerEvents: 'none',
                transition: 'opacity 0.15s ease'
            };
        },
        shipmentIdleStyle() {
            return {
                transition: 'opacity 0.15s ease'
            };
        }
    },
    watch: {
        'cart': {
            immediate: true,
            handler(newCart) {
                if (!newCart) return;
                // Race-hardening: cart prop change supersedes any in-flight enrichment. Bumping
                // enrichmentRunId makes any pending enrichShippingMethodsWithAccuratePrices fail its
                // runId check and abort, preventing stale data from clobbering the new cart state.
                // Also clear any debounced enrichment timer so it re-evaluates against the new cart.
                ++this.enrichmentRunId;
                if (this.cartTotalWatcherTimeout) {
                    clearTimeout(this.cartTotalWatcherTimeout);
                    this.cartTotalWatcherTimeout = null;
                }
                this.internalCart = newCart;
                this.rebuildPrices();
                this.scheduleEnrichmentIfFingerprintChanged();

                // Re-sync the radio to the server's active shipping method when the cart prop
                // arrives from outside this component (voucher apply/remove, line-item changes,
                // parent-driven restore, etc.). Without this, side effects like Acris voucher
                // application that flip cart.deliveries[0].shippingMethod server-side leave the
                // UI radio stuck on the previous selection while CartSummary correctly reflects
                // the new shipping cost — yielding the "Pickup checked, but €3,95 on the bill"
                // desync the customer reported on prod.
                //
                // Skip during in-flight user PATCH (isUpdatingShippingMethod) so we don't revert
                // the radio mid-transition while the user's own PATCH is still en route.
                // Validate the server method exists in our local shippingMethods list before
                // adopting it, so we never set a phantom id during init/before getShippingMethods.
                const serverMethodId = newCart?.deliveries?.[0]?.shippingMethod?.id;
                if (
                    serverMethodId
                    && serverMethodId !== this.selectedShippingMethodId
                    && !this.isUpdatingShippingMethod
                    && this.shippingMethods.some(m => m.id === serverMethodId)
                ) {
                    this.selectedShippingMethodId = serverMethodId;
                }
            },
            deep: true
        },
        'context.customer': {
            handler(newCustomer) {
                if (this.customerWatcherTimeout) {
                    clearTimeout(this.customerWatcherTimeout);
                }
                this.customerWatcherTimeout = setTimeout(() => {
                    if (newCustomer && this.shippingMethods.length > 0) {
                        this.rebuildPrices();
                        this.scheduleEnrichmentIfFingerprintChanged();
                    }
                }, 200);
            },
            deep: true
        }
    },
    created: function () {
        this.init();
    },
    beforeDestroy() {
        if (this.customerWatcherTimeout) {
            clearTimeout(this.customerWatcherTimeout);
        }
        if (this.cartTotalWatcherTimeout) {
            clearTimeout(this.cartTotalWatcherTimeout);
        }
    },
    methods: {
        rebuildPrices() {
            const delivery = this.cartData?.deliveries?.[0];
            const activeMethodId = delivery?.shippingMethod?.id;
            const activePrice = delivery?.shippingCosts?.totalPrice ?? null;
            const fingerprint = this.enrichmentFingerprint;

            // Resolve the displayed price for one shipping method using a priority chain of
            // guard clauses. Each guard returns early on hit; reaching the end means "render the
            // spinner" (null). Order matters — active method's live cost is most authoritative,
            // a fresh-fingerprint cached enrichment value is next, the pickup short-circuit
            // avoids a pointless spinner on an unconditionally-free row, and everything else
            // gets a spinner until background enrichment commits a rule-aware value.
            const resolvePrice = (method) => {
                // 1. Active method: live cost from the cart's own delivery is rule-aware and authoritative.
                if (method.id === activeMethodId && activePrice !== null) return activePrice;

                // 2. Cached enrichment value tagged for THIS cart fingerprint — trust it.
                if (typeof method._accuratePrice === 'number'
                    && method._accuratePriceFingerprint === fingerprint) return method._accuratePrice;

                // 3. Pickup at a garden center ('Free pick up' / 'Gratis afhalen') is unconditionally
                //    free across every active sales channel — there is no rule in this codebase that
                //    could ever make it MORE expensive than its single €0 NULL-rule baseline tier.
                //    Customers paying shipping for in-store pickup is a contradiction, full stop.
                //    Showing a spinner on the Pickup row would falsely suggest a non-zero outcome
                //    and creates pointless cognitive overhead. Display "Gratis" immediately.
                //    'shipping_dealer' is the codebase's established convention for detecting pickup
                //    (see hasDealerOption, isNoDealerSelected, updateShippingMethod in this file,
                //    plus DealerSelector, CheckoutInformation, and Confirmation components).
                if (method.technicalName === 'shipping_dealer') return 0;

                // 4. Default: null → template renders a spinner. We deliberately do NOT fall back to
                //    the rule-blind static price (calculateFallbackPrice) because it only sees
                //    NULL-rule tiers and would misleadingly show €3,95 for Website-NL where the
                //    rule-aware price is actually €0. Pending background enrichment will commit the
                //    correct value within ~1.5s and replace the spinner.
                return null;
            };

            const newPrices = {};
            for (const method of this.shippingMethods) {
                newPrices[method.id] = resolvePrice(method);
            }
            this.prices = newPrices;
        },

        scheduleEnrichmentIfFingerprintChanged() {
            const fingerprint = this.enrichmentFingerprint;
            if (fingerprint === null) return;

            // Normally we only re-enrich when the fingerprint has changed since the last successful run.
            // BUT — we ALSO re-enrich if any non-active method is still missing a fresh-fingerprint
            // _accuratePrice (e.g. previous run returned null for that method, or the cart transitioned
            // from empty → populated under the same fingerprint, like a voucher whose discount cancels
            // out the product price exactly so cart.price.totalPrice stays 0). Otherwise the spinner
            // would persist indefinitely on those rows.
            const activeMethodId = this.cartData?.deliveries?.[0]?.shippingMethod?.id;
            const anyMissing = this.shippingMethods.some(m =>
                m.id !== activeMethodId && (
                    typeof m._accuratePrice !== 'number'
                    || m._accuratePriceFingerprint !== fingerprint
                )
            );
            if (fingerprint === this.lastEnrichmentFingerprint && !anyMissing) return;

            // Debounce: rapid changes (e.g., quantity ticks) coalesce into one re-enrichment
            if (this.cartTotalWatcherTimeout) {
                clearTimeout(this.cartTotalWatcherTimeout);
            }
            this.cartTotalWatcherTimeout = setTimeout(async () => {
                this.cartTotalWatcherTimeout = null;
                if (!this.shippingMethods.length) return;
                // Re-check after debounce — cart may have settled and enrichment may have caught up.
                const fpStillStale = this.enrichmentFingerprint !== this.lastEnrichmentFingerprint;
                const stillMissing = this.shippingMethods.some(m =>
                    m.id !== this.cartData?.deliveries?.[0]?.shippingMethod?.id && (
                        typeof m._accuratePrice !== 'number'
                        || m._accuratePriceFingerprint !== this.enrichmentFingerprint
                    )
                );
                if (!fpStillStale && !stillMissing) return;
                await this.enrichShippingMethodsWithAccuratePrices();
            }, 150);
        },

        async init() {
            // Prevent multiple simultaneous calls
            if (this.isLoading) {
                return;
            }

            this.isLoading = true;
            this.isError = false;

            try {
                // Initialize context to get customer country information
                await this.getContext();

                if (!this.cart) {
                    await this.fetchCart();
                }

                await this.getShippingMethods();
            } catch (error) {
                console.error("Error initializing shipping methods:", error);

                // Retry logic for network errors
                if (this.retryCount < this.maxRetries) {
                    this.retryCount++;
                    console.log(`Retrying shipping methods init (${this.retryCount}/${this.maxRetries})...`);
                    await new Promise(resolve => setTimeout(resolve, 1000)); // Wait before retry
                    await this.init();
                } else {
                    this.isError = true;
                    this.retryCount = 0;
                }
            } finally {
                this.isLoading = false;
            }
        },

        async fetchCart() {
            try {
                this.internalCart = await this.api.client.invoke("readCart get /checkout/cart");
            } catch (error) {
                console.error("Error fetching cart:", error);
                throw error;
            }
        },

        async getShippingMethods() {
            try {
                // Get shipping methods with current context (includes customer address/country)
                const shippingMethods = await this.api.client.invoke("readShippingMethod post /shipping-method?onlyAvailable", {
                    onlyAvailable: true
                });

                if (!shippingMethods || !shippingMethods.elements) {
                    throw new Error("No shipping methods returned");
                }

                this.shippingMethods = shippingMethods.elements;

                // Get accurate prices for each shipping method by calculating with current context
                await this.enrichShippingMethodsWithAccuratePrices();
                
                // Clear cache after enrichment since we now have accurate prices

                // Freeze static nested objects for performance, but NOT the method itself
                // so _accuratePrice can be updated reactively on cart changes
                this.shippingMethods.forEach(method => {
                    Object.freeze(method.translated);
                    Object.freeze(method.deliveryTime);
                });

                // Sort methods by position
                this.sortShippingMethods();

                // Initialize selection from the best available source:
                // 1. Existing component selection (user already picked during this session)
                // 2. Cart's current delivery method (persisted in Shopware context)
                // 3. First available method (fallback for new carts)
                const existingSelectionAvailable = this.selectedShippingMethodId
                    && this.shippingMethods.some(m => m.id === this.selectedShippingMethodId);
                const cartMethodId = this.cartData?.deliveries?.[0]?.shippingMethod?.id || null;
                const cartMethodAvailable = cartMethodId && this.shippingMethods.some(m => m.id === cartMethodId);

                if (existingSelectionAvailable) {
                    // User already selected during this session — keep it
                } else if (cartMethodAvailable) {
                    // Restore from Shopware context (e.g., page refresh)
                    this.selectedShippingMethodId = cartMethodId;
                } else if (this.shippingMethods.length > 0) {
                    // Nothing valid anywhere — pick first and sync with API
                    this.selectedShippingMethodId = this.shippingMethods[0].id;
                    await this.updateShippingMethod(this.selectedShippingMethodId);
                }
            } catch (error) {
                console.error("Error fetching shipping methods:", error);
                throw error;
            }
        },

        async enrichShippingMethodsWithAccuratePrices() {
            if (!this.cartData || !this.shippingMethods.length) {
                return;
            }

            // Take ownership of a new run; older in-flight runs will see runId mismatch and abort.
            // This protects against:
            //   - cart changes mid-enrichment landing prices for a stale total
            //   - manual updateShippingMethod() racing with our context-restore step
            const runId = ++this.enrichmentRunId;
            const startFingerprint = this.enrichmentFingerprint;
            const currentShippingMethodId = this.cartData.deliveries?.[0]?.shippingMethod?.id;

            // Determine the method to restore server context to AFTER probing. Previously the restore
            // PATCH (line ~418) was implicitly gated on currentShippingMethodId being truthy, which is
            // exactly the case it FAILS on empty cart (cartData.deliveries[0] doesn't exist on first
            // page load). That left server context stuck on the LAST probed method (Thuislevering on
            // Pickup-then-Thuislevering iteration order). If the user then pasted a voucher (or any
            // other cart-mutating call) before init's subsequent updateShippingMethod() PATCH won
            // server-side, the voucher API used the stale Thuislevering context and the server
            // recalculated the cart under the wrong shipping method — producing the "Pickup radio +
            // €3,95 in summary" desync the customer reported on prod BE.
            //
            // Computed BEFORE the probe loop because probes PATCH /context on every iteration and may
            // also update the mixin's this.context state. Fallback chain (most authoritative first):
            //   1. cartData.deliveries[0].shippingMethod.id — active method when cart has products
            //   2. selectedShippingMethodId — user's UI selection if it's in our shippingMethods list
            //   3. this.context.shippingMethod.id — SalesChannelContext value (sales channel default
            //      on fresh sessions, last persisted choice for returning customers) if available
            //   4. shippingMethods[0].id — deterministic last resort (API returns position-ordered)
            //
            // For fresh empty-cart init, (1)+(2) are null so we fall through to (3) = sales channel
            // default = Pickup, restoring server context to Pickup. The race window where voucher API
            // could see stale Thuislevering context is closed at the source.
            const selectedMethodAvailable = this.selectedShippingMethodId
                && this.shippingMethods.some(m => m.id === this.selectedShippingMethodId);
            const contextMethodId = this.context?.shippingMethod?.id ?? null;
            const contextMethodAvailable = contextMethodId
                && this.shippingMethods.some(m => m.id === contextMethodId);
            const restoreShippingMethodId = currentShippingMethodId
                || (selectedMethodAvailable ? this.selectedShippingMethodId : null)
                || (contextMethodAvailable ? contextMethodId : null)
                || this.shippingMethods[0]?.id
                || null;

            // Build prices in a local map; commit atomically only if our run is still current
            const localPrices = {};

            // Background enrichment: keep cached _accuratePrice values visible during the recalculation
            // and let them silently swap to fresh values when the loop completes. We deliberately:
            //   - do NOT emit 'updating-shipping-method' — reserved for user-initiated updates so CartSummary
            //     only dims for actions the user took.
            //   - do NOT replace prices with spinners — conventional Amazon/Shopify-style UX shows
            //     stable prices that update silently. Most enrichments after a quantity tick produce the
            //     same _accuratePrice values anyway, so the swap is invisible in the common case.

            try {
                for (const method of this.shippingMethods) {
                    if (method.id === currentShippingMethodId) {
                        const cost = this.cartData.deliveries[0].shippingCosts?.totalPrice;
                        localPrices[method.id] = typeof cost === 'number' ? cost : 0;
                    } else {
                        const price = await this.getAccurateShippingPrice(method.id);
                        if (runId !== this.enrichmentRunId) return; // superseded — abort
                        if (price !== null) {
                            localPrices[method.id] = price;
                        }
                        // null = lookup failed; keep prior _accuratePrice rather than fake "free"
                    }
                }

                if (runId !== this.enrichmentRunId || this.enrichmentFingerprint !== startFingerprint) return;

                // Restore original shipping method context.
                //
                // CRITICAL: gate the restore PATCH on BOTH the runId check AND the user-action flag.
                // Without this, the restore PATCH races with a concurrent updateShippingMethod(): if the
                // user clicks Thuislevering while we're between price-probing and restore, our restore
                // PATCH (back to Pickup) can land server-side AFTER the user's PATCH (to Thuislevering),
                // leaving the server context on Pickup even though the UI radio shows Thuislevering. Result:
                // CartSummary reads cart.deliveries[0].shippingCosts = 0 (Pickup is free) while the price
                // column shows €3,95 — the exact "summary stale" symptom the user reported.
                if (restoreShippingMethodId) {
                    if (runId !== this.enrichmentRunId || this.isUpdatingShippingMethod || this.enrichmentFingerprint !== startFingerprint) {
                        // User is changing shipping method or enrichment was superseded — do not restore.
                        // The user's updateShippingMethod() owns the active context; let its PATCH win.
                        return;
                    }
                    await this.api.client.invoke('updateContext patch /context', {
                        shippingMethodId: restoreShippingMethodId,
                    });
                    if (runId !== this.enrichmentRunId || this.isUpdatingShippingMethod || this.enrichmentFingerprint !== startFingerprint) return;
                    // Cancellation-safe assignment: store readCart result locally, then validate runId,
                    // user-PATCH-in-flight, AND fingerprint before committing to internalCart. Without this,
                    // a stale enrichment whose readCart response arrives AFTER a newer cart prop has flowed
                    // down would clobber internalCart with pre-voucher empty-cart data — cartData() prefers
                    // internalCart over cart prop, so this would visibly rewind the displayed cart.
                    const fetchedCart = await this.api.client.invoke('readCart get /checkout/cart');
                    if (runId !== this.enrichmentRunId || this.isUpdatingShippingMethod || this.enrichmentFingerprint !== startFingerprint) return;
                    this.internalCart = fetchedCart;
                    // Propagate the freshly-fetched cart up to Cart.vue so CartSummary's
                    // cart.deliveries[0].shippingCosts stays in sync with what the server actually has.
                    // Empty-cart guard: enrichment is a background process; we never want it to propagate
                    // an empty server-recalc result (e.g. transient rule mismatch on the deferred
                    // "cart disappears after cookie clear + direct cart entry" path). Removing the last
                    // line item is a USER action that flows through CartLineItems' explicit emit, not here.
                    if (this.internalCart?.lineItems?.length > 0) {
                        EventBus.$emit('cart-updated', this.internalCart);
                    }
                }

                // Atomic commit: write all prices to method objects in one batch, tagged with the fingerprint
                // they were computed for. rebuildPrices() refuses to use a cached value whose fingerprint
                // doesn't match the current cart — this prevents the "stuck Gratis" bug where a stale enrichment
                // from a higher cart total leaks into a lower-total render.
                for (const [methodId, price] of Object.entries(localPrices)) {
                    const method = this.shippingMethods.find(m => m.id === methodId);
                    if (method) {
                        this.$set(method, '_accuratePrice', price);
                        this.$set(method, '_accuratePriceFingerprint', startFingerprint);
                    }
                }

                this.lastEnrichmentFingerprint = startFingerprint;
                this.rebuildPrices();
            } catch (error) {
                console.error('Error enriching shipping methods with prices:', error);
                // Don't update fingerprint on error — next change will retry enrichment
            }
        },

        async getAccurateShippingPrice(shippingMethodId) {
            // Track the in-flight PATCH+GET so updateShippingMethod() can await it. Wrapped in an IIFE-style
            // closure so we share one promise reference between the tracker assignment and the actual await,
            // and so the finally cleanup correctly releases only THIS probe (a newer probe may have already
            // overwritten currentContextProbePromise).
            const probe = (async () => {
                try {
                    await this.api.client.invoke('updateContext patch /context', {
                        shippingMethodId: shippingMethodId,
                    });
                    const updatedCart = await this.api.client.invoke('readCart get /checkout/cart');
                    const price = updatedCart.deliveries?.[0]?.shippingCosts?.totalPrice;
                    return typeof price === 'number' ? price : null;
                } catch (error) {
                    console.error(`Error getting accurate price for shipping method ${shippingMethodId}:`, error);
                    return null; // null = lookup failed; caller must NOT treat as 0/free
                }
            })();
            this.currentContextProbePromise = probe;
            try {
                return await probe;
            } finally {
                if (this.currentContextProbePromise === probe) {
                    this.currentContextProbePromise = null;
                }
            }
        },

        sortShippingMethods() {
            this.shippingMethods.sort((a, b) => {
                const positionA = a.position ?? 999;
                const positionB = b.position ?? 999;
                
                // First sort by position
                if (positionA !== positionB) {
                    return positionA - positionB;
                }
                
                // If positions are the same, prioritize free shipping
                const priceA = this.prices[a.id] || 0;
                const priceB = this.prices[b.id] || 0;
                
                if (priceA === 0 && priceB !== 0) return -1;
                if (priceA !== 0 && priceB === 0) return 1;
                if (priceA !== priceB) return priceA - priceB;
                
                // Finally, sort by technical name for consistency
                const nameA = a.technicalName || a.translated?.name || a.name || '';
                const nameB = b.technicalName || b.translated?.name || b.name || '';
                return nameA.localeCompare(nameB);
            });
        },


        calculateFallbackPrice(shippingMethod) {
            if (this.cartData && this.cartData.price) {
                const cartTotal = this.cartData.price.totalPrice;

                const sortedPrices = [...shippingMethod.prices]
                    .filter(p => !p.ruleId)
                    .sort((a, b) => {
                        const aRange = (a.quantityEnd || Infinity) - (a.quantityStart || 0);
                        const bRange = (b.quantityEnd || Infinity) - (b.quantityStart || 0);
                        return aRange - bRange;
                    });

                const applicablePrice = sortedPrices.find(price => {
                    if (!price.currencyPrice || !price.currencyPrice.length) {
                        return false;
                    }

                    const isCartPriceBased = price.calculation === 2;
                    const rangeStart = price.quantityStart || 0;
                    const rangeEnd = price.quantityEnd || Infinity;
                    const compareValue = isCartPriceBased ? cartTotal : 1;

                    return compareValue >= rangeStart && compareValue <= rangeEnd;
                });

                if (applicablePrice?.currencyPrice?.[0]) {
                    return applicablePrice.currencyPrice[0].gross;
                }
            }

            // Fallback: first valid price
            if (shippingMethod.prices?.[0]?.currencyPrice?.[0]) {
                return shippingMethod.prices[0].currencyPrice[0].gross;
            }

            return 0;
        },


        async updateShippingMethod(shippingMethodId) {
            if (!shippingMethodId || this.isUpdatingShippingMethod) {
                return;
            }

            try {
                this.isUpdatingShippingMethod = true;
                this.pendingShippingMethodId = shippingMethodId;
                EventBus.$emit('updating-shipping-method', true);

                // Invalidate any in-flight enrichment so its 'restore original method' step
                // cannot undo this user selection or commit stale prices.
                this.enrichmentRunId++;

                // Await any in-flight enrichment context probe (PATCH+GET) so its PATCH cannot land
                // server-side AFTER our PATCH below. Without this await, the probe's PATCH for a non-active
                // method could be the LAST write to the server context, leaving the cart's active method
                // mismatched from the user's selection. We swallow probe errors — they're irrelevant here.
                if (this.currentContextProbePromise) {
                    try {
                        await this.currentContextProbePromise;
                    } catch (e) { /* probe error is enrichment's concern, not ours */ }
                }

                await this.api.client.invoke("updateContext patch /context", {
                    shippingMethodId: shippingMethodId,
                });

                // If selecting dealer shipping but no dealer is selected, inform the user
                const selectedMethod = this.shippingMethods.find(m => m.id === shippingMethodId);
                if (selectedMethod && selectedMethod.technicalName === 'shipping_dealer' &&
                    (!this.cartData || !this.cartData.extensions || !this.cartData.extensions.dealer)) {
                    // Redirect to dealer selection if URL is provided
                    if (this.dealerSelectionUrl) {
                        setTimeout(() => {
                            window.location.href = this.dealerSelectionUrl;
                        }, 1500);
                    }
                }

                // Fetch updated cart to reflect shipping changes
                const updatedCart = await this.api.client.invoke("readCart get /checkout/cart");
                this.internalCart = updatedCart;
                this.rebuildPrices();
                // Update transaction ID for tracking
                if (updatedCart && updatedCart.token) {
                    localStorage.setItem("transactionId", updatedCart.token);
                }

                // Inform other components
                EventBus.$emit("cart-updated", updatedCart);

                // Track shipping info update for analytics
                this.trackShippingMethodChange(shippingMethodId);
            } catch (error) {
                console.error("Error updating shipping method:", error);
                EventBus.$emit('trigger-alert', {
                    message: this.$t('checkout.shipping_method_update_error'),
                    state: 'error'
                });

                // Reset to previous selection
                this.selectedShippingMethodId = this.cartData?.deliveries?.[0]?.shippingMethod?.id;
            } finally {
                this.isUpdatingShippingMethod = false;
                this.pendingShippingMethodId = null;
                EventBus.$emit('updating-shipping-method', false);
            }
        },

        trackShippingMethodChange(shippingMethodId) {
            try {
                const selectedMethod = this.shippingMethods.find(m => m.id === shippingMethodId);
                if (!selectedMethod) return;

                const methodName = selectedMethod.translated?.name || selectedMethod.name || 'unknown';
                const shippingPrice = this.prices[selectedMethod.id] || 0;

                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                    'event': 'add_shipping_info',
                    'shipping_tier': methodName,
                    'value': this.cartData?.price?.totalPrice || 0,
                    'shipping': shippingPrice,
                    'currency': 'EUR',
                    'items': this.getGTMItems()
                });
            } catch (error) {
                console.error("Error tracking shipping method change:", error);
                // Non-critical error, don't disrupt the user flow
            }
        },

        getGTMItems() {
            if (!this.cartData || !this.cartData.lineItems) return [];

            return this.cartData.lineItems.map((item, index) => ({
                item_id: item.payload?.productNumber || '',
                item_name: item.label,
                item_list_name: item.payload?.category?.name || (item.payload?.productNumber ? 'insectenbestrijders' : 'voucher'),
                item_brand: item.payload?.manufacturer?.name || "DCM",
                price: item.price?.unitPrice || 0,
                quantity: item.quantity || 1,
                index: index + 1,
                currency: 'EUR',
                item_category: item.payload?.category?.name || (item.payload?.productNumber ? 'insectenbestrijders' : 'voucher')
            }));
        }
    },
};
</script>
